/**
 * Foundation module completion message — hardcoded for exact copy control.
 *
 * Structure: a single streamed message of sibling blocks — the congratulation,
 * an optional `<guide-announcement>` card (built by the shared
 * `components/guideAnnouncementCard.js`, not by this module — every completion
 * surface renders the same one), and the "what's next" menu. BlockStreamBuffer
 * splits the streamed text on `\n\n`, fades each block in sequentially, and
 * applies uniform inter-block spacing.
 *
 * The memory-update card for the path's final section is NOT part of this
 * message — it streams as its own preceding bubble
 * (`ModeOrchestrator._streamPendingUnderstanding`), the same beat a user gets
 * when any other section closes.
 */

/**
 * Congratulation text (first block), plus the guide-intro sentence when a guide
 * card will follow. The sentence mirrors the `topic_complete` prompt's GUIDE
 * SENTENCE contract (generic branch) so both completion surfaces announce a
 * guide the same way; the caller passes `guide` ONLY when
 * `buildGuideAnnouncementBlock` returned non-null for the same summary, so the
 * copy can never promise a card that won't render.
 *
 * @param {Object} params
 * @param {string} params.firstName
 * @param {{title: string, isReady: boolean}|null} [params.guide] - Announced
 *   guide, or null for no guide sentence.
 * @returns {string}
 */
export function resolveFoundationCompletionPreArtifact({ firstName, guide = null }) {
  const base = `Congratulations ${firstName}!\n\nThat wraps up the **Starting Your Journey** path. We've established a solid foundation to work from — your relationship satisfaction, attachment patterns, emotional connection style, and personality profile. Together, these give me a baseline understanding of who you are and how you show up in your relationship.`;
  if (!guide?.title) return base;
  const guideSentence = guide.isReady
    ? `Finishing this path also earned you the ${guide.title} guide — it's ready in your Library.`
    : `Finishing this path also unlocked your ${guide.title} guide — it's being written now and will be ready in your Library in a few minutes.`;
  // Own paragraph (own block beat, directly above the guide card), not a tail
  // on the recap paragraph.
  return `${base}\n\n${guideSentence}`;
}

/**
 * "What's next" menu text (streamed after the congratulation).
 *
 * Uses a title+subtitle markdown bullet pattern. (The intake "common issues"
 * menu once shared this shape; it now renders as a `<common-issues>`
 * structured card instead — see `intakePrompt.js`.) BlockStreamBuffer
 * splits on `\n\n` so each paragraph fades in as its own block; the two
 * bullets live together in a single list block.
 *
 * Bullet order matches the `foundation-complete` button group order so the
 * user's scan maps 1:1 onto the buttons below. Three slots at most:
 *
 *   1. Invite your partner   (only when NOT coupled)
 *   2. Continue discovery    (always)
 *   3. Start a conversation  (always)
 *
 * Invite leads for unlinked users because it is the only item whose payoff
 * depends on someone else's clock: shared understanding needs BOTH partners
 * through this path and its sequel, so every day it is deferred is a day added
 * to the critical path.
 *
 * The card says nothing about the concern the user named at intake: the
 * Foundation → Advice bridge surfaces it itself, leading its topic menu with
 * that concern whenever one is still unworked.
 *
 * Built as a list rather than concatenated strings so the slot order is
 * readable at a glance and cannot drift from
 * {@link resolveFoundationCompletionButtons}.
 *
 * @param {Object} [params]
 * @param {boolean} [params.isCoupled] - Whether the user already has a partner linked.
 * @returns {string}
 */
export function resolveFoundationCompletionPostArtifact({ isCoupled = true } = {}) {
  const bullets = [];
  if (!isCoupled) {
    bullets.push('- **Invite your partner**<br>Everything shared between you starts once they join');
  }
  bullets.push('- **Continue discovery**<br>Keep building your profile with deeper topics');
  bullets.push('- **Start a conversation**<br>Talk through what we\'ve learned so far — beginning with what brought you here');

  return `With this foundation in place, there's a lot more we can do together:\n\n${bullets.join('\n')}\n\nWhat would you like to do next?`;
}

/**
 * Build the `foundation-complete` button list from the catalog entry, producing
 * the same slots {@link resolveFoundationCompletionPostArtifact} produces as
 * bullets: Invite (unlinked only) → Continue → Start a conversation. Three
 * buttons maximum.
 *
 * The two functions MUST stay in step — the group's contract is that bullet
 * order maps 1:1 onto button order — which is why they live in one file and why
 * both the live completion path and `dev.previewFoundationCompletion` call
 * through here rather than splicing independently.
 *
 * The invite button does not live in the server catalog: it carries per-user
 * data (the link state) that only the client holds at render time.
 *
 * @param {Object} params
 * @param {Array<Object>} params.catalogButtons - `foundation-complete` buttons from the registry.
 * @param {boolean} [params.isCoupled] - Whether the user already has a partner linked.
 * @returns {Array<Object>} A new array; the caller's catalog entry is never mutated.
 */
export function resolveFoundationCompletionButtons({ catalogButtons, isCoupled = true }) {
  // Cloned before any unshift — createButtonGroup receives this array directly,
  // so mutating the cached registry entry would corrupt it for later renders.
  // Catalog order is [Continue discovery, Start a conversation].
  const buttons = [...catalogButtons];

  if (!isCoupled) {
    // Leads the group, mirroring the leading invite bullet.
    buttons.unshift({
      id: 'foundation-invite-partner',
      label: 'Invite your partner',
      action: 'open_partner_invite',
      // The only recommended button in this group. foundation-complete is an
      // input-mounted group, so this renders the "Suggested" chip.
      metadata: { recommended: true },
      echo: true
    });
  }

  return buttons;
}

/**
 * Build the button list for a GENERIC (non-Foundation) path completion from the
 * `discovery-complete-next` / `discovery-complete-done` catalog entry.
 *
 * The two groups are structurally different and this resolver owns both shapes:
 *   - `discovery-complete-next`: [Continue → next topic, Choose a different path].
 *     Neither carries `recommended`.
 *   - `discovery-complete-done`: a single `discovery-exit` button that already
 *     carries `metadata.recommended`.
 *
 * The Single-"Suggested" invariant (exactly one `metadata.recommended` per
 * mounted array — `ActionButtons.createButton` chips every recommended button
 * and nothing dedupes) therefore holds over the catalog's own native chips
 * here. The partner-sharing CTA no longer unshifts into this group: the reward
 * lands first and the sharing decision is its own phase 2 beat (a
 * questionnaire-style launch, not a button carried forward from here) that
 * resolves before this array mounts, in phase 3.
 *
 * Transforms, in order:
 *   1. `nextTopicLabel` → relabel `discovery-continue` to name the destination
 *      topic (`load_next_path` serves the ranker's next TOPIC).
 *   2. BETA PATH GATE (`betaGateActive`, from the completion envelope):
 *      `discovery-choose` ("Choose a different path") is REPLACED by a
 *      "Back to dashboard" button — while the gate is on, the paths tab is a
 *      wall of Coming-soon cards. `discovery-exit` is relabeled to match.
 *      Gate off ⇒ catalog buttons pass through untouched.
 *
 * @param {Object} params
 * @param {Array<Object>} params.catalogButtons - Buttons from the registry entry.
 * @param {string|null} [params.nextTopicLabel] - Ranker's next topic, if any.
 * @param {boolean} [params.betaGateActive] - `betaPathGate` from the completion envelope.
 * @returns {Array<Object>} A new array; the caller's catalog entry is never mutated.
 */
export function resolvePathCompletionButtons({
  catalogButtons,
  nextTopicLabel = null,
  betaGateActive = false
}) {
  return catalogButtons.map(btn => {
    let next = btn;
    if (nextTopicLabel && btn.id === 'discovery-continue') {
      next = { ...next, label: `Continue to ${nextTopicLabel}` };
    }
    if (betaGateActive && btn.id === 'discovery-choose') {
      // BETA PATH GATE — see backend/src/config/betaPathGate.js removal manifest.
      next = {
        id: 'discovery-back-dashboard',
        label: 'Back to dashboard',
        action: 'return_to_dashboard'
      };
    }
    if (betaGateActive && btn.id === 'discovery-exit') {
      next = { ...next, label: 'Back to dashboard' };
    }
    return next;
  });
}

/**
 * Phase 2's opening message — the sharing decision, presented alone (§3.G1 of
 * the binary-beat plan). Two variants, `firstOffer` vs returning, joined as
 * one or two `\n\n`-separated blocks the same way
 * {@link resolveFoundationCompletionPostArtifact} composes its bullets: each
 * paragraph is its own BlockStreamBuffer block.
 *
 * `firstOffer` is derived from the completing path (`pathId === 'foundation-2'`)
 * rather than from stored state, because there IS no stored first-run signal:
 * on the genuine first ask and on a re-ask after a prior "Not now", the state
 * looks identical (`gestaltConfirmedAt` null, all six units undecided) — there
 * is nothing in the read model to distinguish them. The completing path is the
 * free discriminator instead, and it is correct for the dominant flow because
 * foundation-2's completion is always the one that first opens the gate. Known
 * miss: a member who finishes both Foundations before ever linking a partner
 * gets the brief "returning" variant on their genuine first ask — accepted,
 * since they will already have been seeing the dashboard nudge for a while by
 * then. The precise alternative (a stored `sharingInvitationSeenAt`) was
 * deliberately not taken: it costs a schema column and a write on the serving
 * path to fix a one-time wording miss for a narrow cohort.
 *
 * Both variants stay GENERAL rather than re-naming the six units: the
 * completion message immediately above this one has just named the specifics,
 * so repeating them here would read as a recap. The first-offer variant keeps
 * the no-surprises guarantee in its own trailing paragraph — it is the line an
 * anxious user needs to actually see, not read past inside a longer sentence.
 *
 * @param {Object} params
 * @param {string|null} params.partnerName - Partner's display name, or null
 *   for the generic "your partner" fallback.
 * @param {boolean} params.firstOffer - Whether this is the path completion
 *   that first opens the sharing gate (`pathId === 'foundation-2'`), vs a
 *   later path completion re-offering it after a prior decline.
 * @returns {string}
 */
export function resolveSharingInvitationMessage({ partnerName, firstOffer }) {
  const who = partnerName || 'your partner';
  if (firstOffer) {
    const intro = `Now that we've built a fuller understanding of you, you can choose to share some of it with ${who}. It's the difference between advice that knows only your side and advice that knows the relationship.`;
    const guarantee = `You'll see the exact words first, and nothing is shared until you approve it.`;
    return `${intro}\n\n${guarantee}`;
  }
  return `Please take a moment to review and approve what you'd like to share with ${who}. Nothing is shared until you approve it.`;
}

/**
 * Phase 3's opening acknowledgement, shown only after a successful share
 * (§3.G2). Two variants that differ in exactly one clause, keyed on whether
 * the partner has already confirmed their own side.
 *
 * The branch exists because the couple-level synthesis only starts drawing on
 * BOTH partners once BOTH have confirmed — until then the consent ledger sits
 * at `waiting` for the other side. Most people sharing for the first time are
 * the FIRST of the couple to confirm, so an unconditional "now the guidance
 * can draw on both of you" would be false for the common case, not an edge
 * case: it promises a synthesis that has not been built yet. `submitConsent`
 * already returns `bothConfirmed`; callers thread it straight through from
 * `onResolved`.
 *
 * @param {Object} params
 * @param {string|null} params.partnerName - Partner's display name, or null
 *   for the generic "your partner" fallback.
 * @param {boolean} params.bothConfirmed - Whether the partner has already
 *   confirmed their own side (from `submitConsent`'s response), i.e. whether
 *   the couple-level synthesis can draw on both sides starting now vs only
 *   once the partner catches up.
 * @returns {string}
 */
export function resolveSharingAcknowledgement({ partnerName, bothConfirmed }) {
  const who = partnerName || 'your partner';
  const middle = bothConfirmed
    ? `and from here the guidance each of you gets can draw on both sides rather than one.`
    : `and once they do the same, the guidance each of you gets can draw on both sides rather than one.`;
  const thanks = `Thank you — that's shared. ${who} can see what you approved, ${middle}`;
  const control = `You can change or withdraw any of it from your Partner page whenever you want.`;
  return `${thanks}\n\n${control}`;
}

/**
 * Phase 2's decline copy — "Not now" (§3.G3). Previously silent; declining
 * the invitation left the beat with no acknowledgement at all, which is a
 * large part of why it read as a dead end.
 *
 * Deliberately does NOT mention that the ask returns at the next path
 * completion: true, but saying so turns a graceful exit into a warning. Hands
 * the floor straight to the options menu that mounts next in phase 3.
 *
 * @returns {string}
 */
export function resolveSharingDeclineMessage() {
  return `That's fine — there's no rush on this one. It'll be waiting on your Partner page whenever you want it.`;
}

/**
 * Phase 2's abandon copy — the questionnaire panel was closed with nothing
 * submitted (§3.G4). Previously silent, same as the decline case.
 *
 * Sits above a re-offered `[Begin again] [Not now]` row, so unlike the decline
 * message it must NOT promise what happens next — the decision is still open,
 * not resolved. This is also the only place the user learns their staged
 * answers are gone; it says so by implication ("Start again") rather than as
 * an explicit warning.
 *
 * @returns {string}
 */
export function resolveSharingAbandonMessage() {
  return `No problem — you closed that before anything was shared. Start again whenever you're ready.`;
}

/**
 * "What's next" options menu for a GENERIC path completion — the counterpart of
 * {@link resolveFoundationCompletionPostArtifact} for the Haiku-message branch.
 * Rendered as its own bubble after the guide card (the Haiku stream cannot be
 * spliced), before the buttons.
 *
 * Derives bullets FROM the already-resolved button array rather than re-running
 * the button conditionals: every emitted bullet maps to exactly one resolved
 * button, preserves resolved-button order, and uses that button's exact label —
 * so the beta-gate relabels and the "Continue to Next Path" null-label fallback
 * each have exactly one home. The mapping is a SUBSET, not a bijection:
 * exit/back buttons stay unlisted (they are exits, not "things we could work
 * on").
 *
 * The sharing bullet went with the sharing splice: the invitation is now its
 * own beat with its own message, not an item in the completion menu.
 *
 * Returns null when fewer than two bullets result — the caller keeps the prose
 * forward-pitch in the completion message instead (`optionsFollow` keys off
 * null-ness, so message, menu, and buttons can never disagree).
 *
 * @param {Object} params
 * @param {Array<Object>|null} params.resolvedButtons - Output of
 *   {@link resolvePathCompletionButtons}; null/non-array yields null (fail-soft).
 * @param {string|null} [params.nextTopicDescription] - Authored topic subtitle
 *   from the ranking result, or null for the generic fallback.
 * @returns {string|null} Markdown menu block, or null when the list would not
 *   render (fewer than two substantive options).
 */
export function resolvePathCompletionOptionsList({ resolvedButtons, nextTopicDescription = null }) {
  if (!Array.isArray(resolvedButtons)) return null;
  const byId = (id) => resolvedButtons.find(btn => btn.id === id) || null;

  const bullets = [];

  const continueButton = byId('discovery-continue');
  if (continueButton) {
    bullets.push(`- **${continueButton.label}**<br>${nextTopicDescription || 'Keep building your profile with deeper topics'}`);
  }

  // Post-beta only by construction: the beta gate replaces this button with a
  // dashboard exit (different id), and journey_complete's catalog group never
  // contains it — no scenario check needed here.
  const chooseButton = byId('discovery-choose');
  if (chooseButton) {
    bullets.push(`- **${chooseButton.label}**<br>Browse everything else on your discovery page`);
  }

  if (bullets.length < 2) return null;

  return `You have a few options for things we could work on next:\n\n${bullets.join('\n')}\n\nWhat would you like to do next?`;
}


